import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Mic, Pause, Play, SendHorizontal, X } from 'lucide-react';
import { LIMITS, VOICE_TERMINAL_STATUSES, type VoiceJob } from '@worki/shared';
import { useDialogBehaviour } from './Modal.js';
import { VoiceOrb, type VoiceOrbMode } from './voice/VoiceOrb.js';
import { useVoiceRecorder } from '../lib/useVoiceRecorder.js';
import type { VoiceRecorderState } from '../lib/voiceRecorder.js';
import { jobStillRunning, voiceApi } from '../lib/voice.js';
import { RequestError } from '../lib/api.js';
import { cn } from '../lib/cn.js';
import { AgentActionBalance } from './AgentActionBalance.js';
import { agentApi } from '../lib/agent.js';
import { useAuthenticated } from '../lib/session.js';

/**
 * VOICE-001 — the live voice screen: speak, and watch the note being heard.
 *
 * It opens listening, like a voice assistant does, with an orb at its centre
 * that follows the microphone; the words under the orb say the same thing the
 * orb shows, for whoever is not looking at it. Once the take is sent the orb
 * turns while the note is transcribed and understood, the words heard appear
 * one by one under "what we heard" as soon as they are known, and when the
 * draft is ready the orb folds into a check and the screen hands over.
 *
 * With `dictate` it is the same screen for one field's worth of words: there
 * is no understanding stage and no transcript to read before the hand-over —
 * the text is going into the field, where it can be read and edited.
 *
 * Closing it cancels the job, so a note nobody is waiting for does not hold
 * the queue or the quota.
 *
 * A panel in the middle of the page on a desktop; the whole screen on a phone,
 * where a sheet with a 300px orb in it would be all orb and no sheet. Every
 * colour is a theme token, so it is the user's accent in the user's mode.
 */

/** What the orb should be doing, from everything the screen knows. */
export function voiceScreenMode({
  recorder,
  uploading,
  uploadError,
  job,
}: {
  recorder: VoiceRecorderState;
  uploading: boolean;
  uploadError: string | null;
  job: VoiceJob | null | undefined;
}): VoiceOrbMode {
  if (uploadError) return 'failed';
  if (job) {
    if (job.status === 'ready') return 'ready';
    if (job.status === 'failed' || job.status === 'cancelled') return 'failed';
    return 'busy';
  }
  if (uploading) return 'busy';
  if (recorder === 'recording') return 'listening';
  if (recorder === 'paused') return 'paused';
  if (recorder === 'denied') return 'failed';
  return 'idle';
}

function jobCaption(t: TFunction, job: VoiceJob): string {
  return job.status === 'failed' && job.errorKey
    ? t(job.errorKey)
    : t(`voice.stage.${job.status}`, { position: job.queuePosition ?? 1 });
}

/**
 * The words heard, revealed one at a time. The whole sentence is also there,
 * once, for a screen reader — read word-span by word-span it would be a list
 * of single words with pauses between them.
 *
 * The reveal is quick on purpose: 70ms a word reads as the sentence arriving,
 * but a two-minute note is a few hundred words, so the gap shrinks to keep the
 * whole reveal within about two and a half seconds.
 */
function VoiceHeard({ transcript }: { transcript: string }) {
  const { t } = useTranslation();
  const words = transcript.split(/\s+/).filter(Boolean);
  const step = Math.min(70, 2500 / Math.max(1, words.length));
  return (
    <div className="mx-auto w-full max-w-md px-6">
      <p className="mb-1 text-center text-xs tracking-wide text-muted-foreground">{t('voice.heard')}</p>
      <p className="sr-only">{transcript}</p>
      <p
        aria-hidden="true"
        className="max-h-28 overflow-y-auto text-center text-[15px] leading-7 text-foreground md:max-h-24"
      >
        {words.map((word, i) => (
          <span key={i}>
            <span className="voice-word" style={{ animationDelay: `${Math.round(i * step)}ms` }}>
              {word}
            </span>{' '}
          </span>
        ))}
      </p>
    </div>
  );
}

/**
 * The line under the orb and, once there is one, the transcript. `caption`
 * overrides what the job would say — the screen has states before there is a
 * job (listening, paused, sending) and errors that never reach one.
 *
 * The live region is the stable outer line; the text inside it is keyed so a
 * new caption fades in, and a region that stays put is the one a screen reader
 * reliably announces.
 */
export function VoiceJobStage({ job, caption }: { job?: VoiceJob | null; caption?: string }) {
  const { t } = useTranslation();
  const text = caption ?? (job ? jobCaption(t, job) : '');
  return (
    <div className="space-y-3">
      <p aria-live="polite" className="min-h-8 px-6 text-center text-xl font-medium text-foreground">
        <span key={text} className="voice-caption inline-block">
          {text}
        </span>
      </p>
      {job?.transcript && <VoiceHeard transcript={job.transcript} />}
    </div>
  );
}

function clock(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
}

/** A round control with its name written under it, as on a phone's call screen. */
function Control({
  label,
  hidden,
  main,
  onClick,
  children,
  autoFocus,
}: {
  label: string;
  hidden?: boolean;
  main?: boolean;
  onClick(): void;
  children: ReactNode;
  autoFocus?: boolean;
}) {
  return (
    <div
      className={cn(
        // A control that goes away gives its room back, so what is left — cancel,
        // while the note is worked on — stands in the middle, not off to a side.
        'flex w-24 flex-col items-center gap-1.5 transition-[opacity,transform,width] duration-300',
        hidden && 'pointer-events-none w-0 scale-75 opacity-0',
      )}
      aria-hidden={hidden || undefined}
    >
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        disabled={hidden}
        data-autofocus={autoFocus || undefined}
        className={cn(
          'grid place-items-center rounded-full transition-[transform,background-color,box-shadow] duration-150 active:scale-[0.94] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card',
          main
            ? 'voice-main h-[72px] w-[72px] bg-primary text-primary-foreground dark:bg-white dark:text-primary'
            : 'h-14 w-14 bg-foreground/[0.07] text-foreground hover:bg-foreground/[0.12] dark:bg-white/10 dark:hover:bg-white/[0.16]',
        )}
      >
        {children}
      </button>
      <span aria-hidden="true" className="text-xs text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

export function VoiceJobDialog({
  quoteId,
  snapshot,
  dictate,
  onReady,
  onUseAsNotes,
  onClose,
}: {
  quoteId?: string;
  dictate?: boolean;
  snapshot?: Array<{ description: string; quantity: number; unitPrice: number }>;
  onReady(job: VoiceJob): void;
  onUseAsNotes?(transcript: string): void;
  onClose(): void;
}) {
  const { t } = useTranslation();
  // Set when the person closes. An upload still in flight then creates a job
  // nobody wants; it is cancelled the moment the server names it.
  const aborted = useRef(false);
  // The limit stops the take on its own, so its blob arrives here and not
  // through stop(). It goes up exactly as a take the person ended.
  const recorder = useVoiceRecorder(LIMITS.maxVoiceSeconds, (blob) => void upload(blob));
  const [jobId, setJobId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLDivElement>(null);
  const againRef = useRef<HTMLButtonElement>(null);
  const titleId = 'voice-live-title';
  const queryClient = useQueryClient();
  const session=useAuthenticated();
  const quotaKey=['agent','quota',session.user.id,session.business.id];
  const allowance=useQuery({queryKey:quotaKey,queryFn:agentApi.quota,retry:false,refetchInterval:15000});
  const refreshAllowance=()=>{void queryClient.invalidateQueries({queryKey:quotaKey});void queryClient.invalidateQueries({queryKey:['billing']});};

  const job = useQuery({
    queryKey: ['voice-job', jobId],
    queryFn: () => voiceApi.get(jobId!),
    enabled: jobId !== null,
    // The event stream usually gets here first; this is the floor.
    refetchInterval: (query) =>
      query.state.data && VOICE_TERMINAL_STATUSES.includes(query.state.data.status) ? false : 3000,
  });
  const current = jobId ? job.data : undefined;
  useEffect(()=>{refreshAllowance();},[current?.status]);

  // The screen opens listening: the person pressed the orb to talk, and a
  // second "start" button between them and talking is a step with nothing in
  // it. The permission prompt, the first time, is the browser's own.
  const { start } = recorder;
  useEffect(() => {
    void start();
  }, [start]);

  // Only the status turning ready matters; `onReady` is the caller's inline
  // arrow, whose identity changes every render and must not re-fire this. The
  // hand-over waits for the orb to fold into its check mark — the one moment
  // the screen says "done" without words, and it would be cut off otherwise.
  const readyJob = useRef<VoiceJob | null>(null);
  readyJob.current = current?.status === 'ready' ? current : null;
  useEffect(() => {
    if (current?.status !== 'ready') return undefined;
    const timer = setTimeout(() => {
      if (readyJob.current) onReady(readyJob.current);
    }, 700);
    return () => clearTimeout(timer);
  }, [current?.status]);

  const upload = async (blob: Blob | null) => {
    if (!blob) {
      setUploading(false);
      return;
    }
    setUploading(true);
    try {
      const created = await voiceApi.create(
        blob,
        dictate ? { kind: 'dictate' } : quoteId ? { quoteId, snapshot: snapshot ?? [] } : undefined,
      );
      if (aborted.current) {
        voiceApi.cancel(created.id).finally(refreshAllowance).catch(() => undefined);
        return;
      }
      // The job as the server just named it is the first thing to show; left
      // to the first poll, the screen sat for a moment between "sending" and
      // the queue with nothing to say and the orb at rest.
      queryClient.setQueryData(['voice-job', created.id], created);
      setJobId(created.id);
    } catch (error) {
      if (!aborted.current) setUploadError(error instanceof RequestError ? error.code : 'errors.unknown');
    } finally {
      if (!aborted.current) setUploading(false);
    }
  };

  // Sending is set before the recorder is asked to stop, not after: between
  // the two the recorder already reads idle, and for that one render the
  // screen looked like it had never started — hint back, "start" in the
  // middle — before it turned to "sending".
  const finish = async () => {
    setUploading(true);
    await upload(await recorder.stop());
  };

  const close = () => {
    aborted.current = true;
    recorder.discard();
    if (jobStillRunning(jobId, job.data)) voiceApi.cancel(jobId).finally(refreshAllowance).catch(() => undefined);
    onClose();
  };

  /*
   * Not every way out is close(): Android's back button on the new-quote
   * dialog, or leaving the editor, unmounts the screen without it. The job
   * would then run to ready and be counted with nobody there to receive it, so
   * leaving cancels it the same way. Read through a ref because the cleanup
   * only runs once, with the values of the last render.
   *
   * `aborted` is reset on mount because StrictMode mounts, unmounts and mounts
   * again in development; without it the rehearsal would mark the real screen
   * closed and its first upload would be cancelled.
   */
  const leaving = useRef({ jobId, job: job.data });
  leaving.current = { jobId, job: job.data };
  useEffect(() => {
    aborted.current = false;
    // Nothing waits on it: a server without the GPU service ignores it.
    voiceApi.warm().catch(() => undefined);
    return () => {
      if (aborted.current) return; // close() already did this.
      aborted.current = true;
      const { jobId: id, job: data } = leaving.current;
      if (jobStillRunning(id, data)) voiceApi.cancel(id).finally(refreshAllowance).catch(() => undefined);
    };
  }, []);

  const again = () => {
    setJobId(null);
    setUploadError(null);
    void recorder.start();
  };

  useDialogBehaviour(true, dialogRef, close);

  const mode = voiceScreenMode({ recorder: recorder.state, uploading, uploadError, job: current });
  const live = mode === 'listening' || mode === 'paused';
  const failedJob = uploadError !== null || current?.status === 'failed' || current?.status === 'cancelled';
  const canStart = !jobId && !uploading && !uploadError && (recorder.state === 'idle' || recorder.state === 'denied');
  // Dictation does not show the words back: they are on their way to the field.
  const shown = dictate && current ? { ...current, transcript: null } : current;
  const compact = Boolean(shown?.transcript) || failedJob;

  // When the controls a person was using go away — the take was sent — focus
  // would fall to the page behind the dialog; it moves to cancel, what is left.
  // A failure moves it on to "record again", the way forward from there.
  useEffect(() => {
    const active = document.activeElement;
    if (active && active !== document.body && !(active as HTMLButtonElement).disabled) return;
    cancelRef.current?.querySelector('button')?.focus();
  }, [live, mode]);
  useEffect(() => {
    if (failedJob) againRef.current?.focus();
  }, [failedJob]);

  const caption = uploadError
    ? t(uploadError, { defaultValue: t('errors.unknown') })
    : current
      ? undefined
      : uploading
        ? t('voice.sending')
        : recorder.state === 'recording'
          ? t('voice.listening')
          : recorder.state === 'paused'
            ? t('voice.paused')
            : recorder.state === 'denied'
              ? t('voice.micDenied')
              : '';

  const max = LIMITS.maxVoiceSeconds;
  const ring = 2 * Math.PI * 48;

  // z-[80], over the phone's bottom bar (z-[70]): other dialogs stop short of
  // the bar, but this one is the whole screen on a phone, and its controls sit
  // exactly where the bar is.
  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-foreground/30 backdrop-blur-[3px] animate-in fade-in duration-200 dark:bg-black/60 md:p-4"
      onMouseDown={(event) => {
        // The backdrop closes the dialog as every other one does — except
        // during a take, where a stray click beside the panel would throw
        // away a minute of talking. Escape and cancel still close it then.
        if (event.target === event.currentTarget && !live) close();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'voice-panel voice-rise relative flex h-full w-full flex-col overflow-hidden text-foreground',
          'pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]',
          'md:h-[640px] md:max-h-full md:w-[440px] md:rounded-[28px] md:shadow-2xl md:ring-1 md:ring-border/60 dark:md:ring-white/[0.06]',
        )}
      >
        <header className="flex items-center justify-between gap-3 px-5 pt-5">
          <h2 id={titleId} className="text-base font-semibold text-primary">
            {t(dictate ? 'quotes.dictate' : 'voice.record')}
          </h2>
          <span
            dir="ltr"
            className={cn(
              'rounded-full bg-foreground/[0.06] px-3 py-1.5 text-[13px] tabular-nums text-muted-foreground transition-opacity duration-200 dark:bg-white/[0.08]',
              !live && 'opacity-0',
            )}
            aria-hidden={!live || undefined}
          >
            {clock(recorder.seconds)} / {clock(max)}
          </span>
        </header>

        {/*
          One column, centred in the room between the header and the controls,
          so on a tall phone the orb and its words sit together in the middle
          instead of the orb taking the middle and the words the bottom. Once
          there are words to read — the transcript, a failure and its way out —
          the orb steps back to make room for them rather than pushing them off
          the panel.
        */}
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 overflow-y-auto py-3">
          <p
            className={cn(
              'max-h-14 overflow-hidden px-6 text-center text-[15px] text-muted-foreground transition-[opacity,max-height] duration-300',
              !(live || canStart) && 'max-h-0 opacity-0',
            )}
          >
            {t(dictate ? 'voice.dictateHint' : quoteId ? 'voice.editHint' : 'voice.recordHint')}
          </p>

          <div
            className={cn(
              'relative aspect-square shrink-0 transition-[width] duration-500 ease-out',
              compact ? 'w-[min(60vw,28dvh,240px)] md:w-[220px]' : 'w-[min(80vw,42dvh,340px)] md:w-[min(290px,42vh)]',
            )}
          >
            <svg
              viewBox="0 0 100 100"
              aria-hidden="true"
              className={cn('pointer-events-none absolute inset-0 -rotate-90 transition-opacity duration-300', !live && 'opacity-0')}
            >
              <circle cx="50" cy="50" r="48" fill="none" strokeWidth="1" className="stroke-foreground/[0.07] dark:stroke-white/[0.08]" />
              <circle
                cx="50"
                cy="50"
                r="48"
                fill="none"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeDasharray={ring}
                strokeDashoffset={ring * (1 - Math.min(1, recorder.seconds / max))}
                style={{ stroke: 'hsl(var(--primary-text, var(--primary)))', transition: 'stroke-dashoffset 1s linear' }}
              />
            </svg>
            <VoiceOrb mode={mode} level={recorder.level} className="absolute inset-[4%] h-[92%] w-[92%]" />
          </div>

          <VoiceJobStage job={shown} caption={caption} />
          <AgentActionBalance quota={allowance.data} loading={allowance.isLoading} unavailable={allowance.isError}/>

          {failedJob && (
            <div className="flex flex-wrap justify-center gap-2 px-6">
              <button
                ref={againRef}
                type="button"
                onClick={again}
                className="rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-transform active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
              >
                {t('voice.recordAgain')}
              </button>
              {current?.transcript && onUseAsNotes && (
                <button
                  type="button"
                  onClick={() => onUseAsNotes(current.transcript!)}
                  className="rounded-full bg-foreground/[0.07] px-5 py-2.5 text-sm font-medium transition-transform hover:bg-foreground/[0.12] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:bg-white/10"
                >
                  {t('voice.useTranscript')}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-start justify-center px-4 pb-7 pt-3">
          <div ref={cancelRef}>
            <Control label={t('voice.cancel')} hidden={mode === 'ready'} onClick={close}>
              <X className="h-6 w-6" strokeWidth={2} />
            </Control>
          </div>
          {canStart ? (
            <Control label={t('voice.start')} main autoFocus onClick={() => void recorder.start()}>
              <Mic className="h-7 w-7" />
            </Control>
          ) : (
            <Control label={t('voice.send')} main autoFocus hidden={!live} onClick={() => void finish()}>
              <SendHorizontal className="h-7 w-7 rtl:-scale-x-100" />
            </Control>
          )}
          <Control
            label={t(mode === 'paused' ? 'voice.resume' : 'voice.pause')}
            hidden={!live}
            onClick={mode === 'paused' ? recorder.resume : recorder.pause}
          >
            {mode === 'paused' ? <Play className="h-6 w-6" /> : <Pause className="h-6 w-6" />}
          </Control>
        </div>
      </div>
    </div>,
    document.body,
  );
}
